<template>
  <div class="order-goods-management">
    <div class="page-header">
      <h2>订单商品管理</h2>
    </div>

    <!-- 搜索表单 -->
    <el-form :inline="true" :model="searchForm" class="search-form">
      <el-form-item label="订单号">
        <el-input v-model="searchForm.order_no" placeholder="请输入订单号" clearable />
      </el-form-item>
      <el-form-item label="商品名称">
        <el-input v-model="searchForm.goods_name" placeholder="请输入商品名称" clearable />
      </el-form-item>
      <el-form-item label="用户手机号">
        <el-input v-model="searchForm.user_phone" placeholder="请输入用户手机号" clearable />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleSearch">搜索</el-button>
        <el-button @click="handleReset">重置</el-button>
      </el-form-item>
    </el-form>

    <!-- 数据表格 -->
    <el-table v-loading="loading" :data="orderGoodsList" border style="width: 100%">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="order_no" label="订单号" width="180" />
      <el-table-column prop="goods_name" label="商品名称" />
      <el-table-column prop="goods_image" label="商品图片" width="100">
        <template slot-scope="scope">
          <el-image v-if="scope.row.goods_image" :src="scope.row.goods_image" style="width: 60px; height: 60px" fit="cover" />
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column prop="goods_price" label="单价" width="100">
        <template slot-scope="scope">
          ¥{{ scope.row.goods_price }}
        </template>
      </el-table-column>
      <el-table-column prop="goods_number" label="数量" width="80" />
      <el-table-column prop="goods_amount" label="小计" width="100">
        <template slot-scope="scope">
          ¥{{ scope.row.goods_amount }}
        </template>
      </el-table-column>
      <el-table-column prop="user_name" label="用户名" width="120" />
      <el-table-column prop="user_phone" label="用户手机号" width="120" />
      <el-table-column prop="create_time" label="创建时间" width="180" />
      <el-table-column label="操作" width="120" fixed="right">
        <template slot-scope="scope">
          <div class="table-operations">
            <el-button size="mini" type="text" @click="handleView(scope.row)">查看</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        :current-page="pagination.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.limit"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 查看详情对话框 -->
    <el-dialog title="订单商品详情" :visible.sync="viewDialogVisible" width="800px">
      <div v-if="currentOrderGoods" class="order-goods-detail">
        <!-- 商品信息 -->
        <div class="detail-section">
          <div class="section-title">商品信息</div>
          <div class="goods-info-card">
            <div class="goods-image-wrapper">
              <el-image 
                v-if="currentOrderGoods.goods_image" 
                :src="currentOrderGoods.goods_image" 
                style="width: 160px; height: 160px; border-radius: 8px;"
                fit="cover"
                :preview-src-list="[currentOrderGoods.goods_image]"
              />
              <div v-else class="no-image">暂无图片</div>
            </div>
            <div class="goods-info-content">
              <h3 class="goods-name">{{ currentOrderGoods.goods_name }}</h3>
              <div v-if="currentOrderGoods.goods_spec" class="goods-spec">
                <el-tag size="small" type="info">{{ currentOrderGoods.goods_spec }}</el-tag>
              </div>
              <div class="goods-price-info">
                <div class="price-item">
                  <span class="label">单价：</span>
                  <span class="price-value">¥{{ currentOrderGoods.goods_price }}</span>
                </div>
                <div class="price-item">
                  <span class="label">数量：</span>
                  <span class="quantity-value">×{{ currentOrderGoods.goods_number }}</span>
                </div>
                <div class="price-item">
                  <span class="label">小计：</span>
                  <span class="amount-highlight">¥{{ currentOrderGoods.goods_amount }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 订单信息 -->
        <div class="detail-section">
          <div class="section-title">订单信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">订单ID</div>
              <div class="detail-value">{{ currentOrderGoods.order_id || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">订单号</div>
              <div class="detail-value">{{ currentOrderGoods.order_no || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">订单状态</div>
              <div class="detail-value">
                <el-tag v-if="currentOrderGoods.order_status !== undefined" :type="getOrderStatusType(currentOrderGoods.order_status)">
                  {{ getOrderStatusText(currentOrderGoods.order_status) }}
                </el-tag>
                <span v-else>-</span>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">支付状态</div>
              <div class="detail-value">
                <el-tag v-if="currentOrderGoods.pay_status !== undefined" :type="currentOrderGoods.pay_status == 1 ? 'success' : 'danger'">
                  {{ currentOrderGoods.pay_status == 1 ? '已支付' : '未支付' }}
                </el-tag>
                <span v-else>-</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 用户信息 -->
        <div class="detail-section">
          <div class="section-title">用户信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">用户ID</div>
              <div class="detail-value">{{ currentOrderGoods.user_id || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">用户名</div>
              <div class="detail-value">{{ currentOrderGoods.user_name || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">用户手机号</div>
              <div class="detail-value">{{ currentOrderGoods.user_phone || currentOrderGoods.user_mobile || '-' }}</div>
            </div>
          </div>
        </div>

        <!-- 店铺信息 -->
        <div v-if="currentOrderGoods.shop_id || currentOrderGoods.shop_name" class="detail-section">
          <div class="section-title">店铺信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">店铺ID</div>
              <div class="detail-value">{{ currentOrderGoods.shop_id || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">店铺名称</div>
              <div class="detail-value">{{ currentOrderGoods.shop_name || '-' }}</div>
            </div>
          </div>
        </div>

        <!-- 其他信息 -->
        <div class="detail-section">
          <div class="section-title">其他信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">商品ID</div>
              <div class="detail-value">{{ currentOrderGoods.goods_id || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">SKU ID</div>
              <div class="detail-value">{{ currentOrderGoods.sku_id || '-' }}</div>
            </div>
            <div v-if="currentOrderGoods.remark" class="detail-row">
              <div class="detail-label">备注</div>
              <div class="detail-value">{{ currentOrderGoods.remark }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">创建时间</div>
              <div class="detail-value">{{ currentOrderGoods.create_time || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">更新时间</div>
              <div class="detail-value">{{ currentOrderGoods.update_time || '-' }}</div>
            </div>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="viewDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getOrderGoodsList, getOrderGoodsDetail } from '@/api/store'

export default {
  name: 'OrderGoodsManagement',
  data() {
    return {
      loading: false,
      orderGoodsList: [],
      searchForm: {
        order_no: '',
        goods_name: '',
        user_phone: ''
      },
      pagination: {
        page: 1,
        limit: 20,
        total: 0
      },
      viewDialogVisible: false,
      currentOrderGoods: null
    }
  },
  mounted() {
    this.fetchOrderGoodsList()
  },
  methods: {
    async fetchOrderGoodsList() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.page,
          limit: this.pagination.limit,
          ...this.searchForm
        }
        const response = await getOrderGoodsList(params)
        this.orderGoodsList = response.data.list || []
        this.pagination.total = response.data.total || 0
      } catch (error) {
        console.error('获取订单商品列表失败:', error)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.page = 1
      this.fetchOrderGoodsList()
    },
    handleReset() {
      this.searchForm = {
        order_no: '',
        goods_name: '',
        user_phone: ''
      }
      this.pagination.page = 1
      this.fetchOrderGoodsList()
    },
    handleSizeChange(size) {
      this.pagination.limit = size
      this.fetchOrderGoodsList()
    },
    handleCurrentChange(page) {
      this.pagination.page = page
      this.fetchOrderGoodsList()
    },
    async handleView(row) {
      try {
        const response = await getOrderGoodsDetail(row.id)
        this.currentOrderGoods = response.data
        this.viewDialogVisible = true
      } catch (error) {
        this.$message.error('获取订单商品详情失败')
        console.error('获取订单商品详情失败:', error)
      }
    },
    getOrderStatusType(status) {
      const statusMap = {
        0: 'warning',  // 待付款
        1: 'primary',  // 待发货
        2: 'info',     // 待收货
        3: 'success',  // 已完成
        4: 'danger',   // 已取消
        5: 'warning'   // 售后中
      }
      return statusMap[status] || 'info'
    },
    getOrderStatusText(status) {
      const statusMap = {
        0: '待付款',
        1: '待发货',
        2: '待收货',
        3: '已完成',
        4: '已取消',
        5: '售后中'
      }
      return statusMap[status] || '未知'
    }
  }
}
</script>

<style scoped>
.order-goods-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 24px;
  color: #333;
}

.search-form {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

.order-goods-detail {
  padding: 10px 0;
}

.detail-section {
  margin-bottom: 30px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 15px;
  padding-left: 10px;
  border-left: 4px solid #409EFF;
  line-height: 20px;
}

.goods-info-card {
  display: flex;
  gap: 20px;
  padding: 20px;
  background: #f5f7fa;
  border-radius: 8px;
  border: 1px solid #EBEEF5;
}

.goods-image-wrapper {
  flex-shrink: 0;
}

.no-image {
  width: 160px;
  height: 160px;
  background: #f0f0f0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 14px;
}

.goods-info-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.goods-name {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 10px 0;
  line-height: 1.4;
}

.goods-spec {
  margin-bottom: 15px;
}

.goods-price-info {
  display: flex;
  gap: 30px;
  align-items: center;
}

.price-item {
  display: flex;
  align-items: baseline;
}

.price-item .label {
  font-size: 14px;
  color: #606266;
  margin-right: 5px;
}

.price-value {
  font-size: 20px;
  font-weight: 600;
  color: #409EFF;
}

.quantity-value {
  font-size: 18px;
  font-weight: 600;
  color: #606266;
}

.amount-highlight {
  font-size: 22px;
  font-weight: bold;
  color: #f56c6c;
}

.detail-table {
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  overflow: hidden;
  background-color: #fff;
}

.detail-row {
  display: flex;
  border-bottom: 1px solid #EBEEF5;
  min-height: 50px;
  align-items: center;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  font-weight: 500;
  color: #606266;
  width: 150px;
  text-align: right;
  padding: 15px 20px;
  background-color: #FAFAFA;
  border-right: 1px solid #EBEEF5;
  flex-shrink: 0;
  box-sizing: border-box;
}

.detail-value {
  color: #303133;
  word-break: break-word;
  padding: 15px 20px;
  flex: 1;
  box-sizing: border-box;
}

.dialog-footer {
  text-align: right;
}

.table-operations {
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: nowrap;
}

.table-operations .el-button--text {
  padding: 2px 8px;
  font-size: 12px;
  border: none;
  background: none;
}

.table-operations .edit-btn {
  color: #409eff;
}

.table-operations .edit-btn:hover {
  color: #66b1ff;
  background-color: #ecf5ff;
}

.table-operations .delete-btn {
  color: #f56c6c;
}

.table-operations .delete-btn:hover {
  color: #f78989;
  background-color: #fef0f0;
}
</style>